import { useParams, Link } from 'react-router';
export { careerDetailHandle as handle } from '../routeMetadata';
import { getCareerById } from '../data/availableCareers';
import { useState } from 'react';
import ConfirmationModal from '../components/ConfirmationModal';

const MAX_FILE_SIZE = 4.5 * 1024 * 1024; // 4.5MB

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:application/pdf;base64,")
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export default function CareerDetail() {
  const { careerName } = useParams();
  const career = careerName ? getCareerById(careerName) : undefined;

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whyWorkHere, setWhyWorkHere] = useState('');
  const [experience, setExperience] = useState('');
  const [education, setEducation] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [otherDocsFile, setOtherDocsFile] = useState<File | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    // Validate file sizes before submitting
    if (resumeFile && resumeFile.size > MAX_FILE_SIZE) {
      setError('Resume file is too large (max 4.5MB)');
      return;
    }
    if (otherDocsFile && otherDocsFile.size > MAX_FILE_SIZE) {
      setError('Other documentation file is too large (max 4.5MB)');
      return;
    }

    setSubmitting(true);

    try {
      // Convert files to base64 for upload through the lambda
      let resumeData = '';
      let resumeName = '';
      let otherDocsData = '';
      let otherDocsName = '';

      if (resumeFile) {
        resumeData = await fileToBase64(resumeFile);
        resumeName = resumeFile.name;
      }
      if (otherDocsFile) {
        otherDocsData = await fileToBase64(otherDocsFile);
        otherDocsName = otherDocsFile.name;
      }

      const res = await fetch('/.netlify/functions/submitJobApplication', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          whyWorkHere,
          experience,
          education,
          resumeData,
          resumeName,
          otherDocsData,
          otherDocsName,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to submit application');
      }

      setShowConfirmation(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setWhyWorkHere('');
      setExperience('');
      setEducation('');
      setResumeFile(null);
      setOtherDocsFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  if (!career) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white/90 rounded-2xl shadow-xl p-12 text-center border border-blue-100">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Career Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The career position you're looking for doesn't exist or may have been removed.
          </p>
          <Link
            to="/careers"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            View All Careers
          </Link>
        </div>
      </div>
    );
  }

  const inputClass = "w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        confirmationText="Thank you for applying!"
        title="Application Submitted"
      />
      {/* Back navigation */}
      <div className="mb-8">
        <Link
          to="/careers"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
        >
          ← Back to Careers
        </Link>
      </div>

      {/* Job header */}
      <div className="bg-white/90 rounded-2xl shadow-xl p-8 mb-8 border border-blue-100">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h1 className="text-3xl font-bold text-blue-900">
            {career.title}
          </h1>
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            {career.type}
          </span>
        </div>

        <div className="flex flex-wrap gap-6 mb-6 text-gray-600">
          <div className="flex items-center gap-2">
            <span className="font-medium">Department:</span>
            <span>{career.department}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Location:</span>
            <span>{career.location}</span>
          </div>
          {career.salary && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Salary:</span>
              <span className="text-green-600 font-medium">{career.salary}</span>
            </div>
          )}
        </div>

        <p className="text-lg text-gray-700 leading-relaxed">
          {career.description}
        </p>
      </div>

      {/* Job details */}
      <div className="grid lg:grid-cols-2 gap-8 mb-12">
        {/* Responsibilities */}
        <div className="bg-white/90 rounded-2xl shadow-xl p-8 border border-blue-100">
          <h2 className="text-xl font-bold text-blue-900 mb-4">
            Key Responsibilities
          </h2>
          <ul className="space-y-3">
            {career.responsibilities.map((responsibility, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium mt-0.5 flex-shrink-0">
                  {index + 1}
                </span>
                <span className="text-gray-700">{responsibility}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Requirements */}
        <div className="bg-white/90 rounded-2xl shadow-xl p-8 border border-blue-100">
          <h2 className="text-xl font-bold text-blue-900 mb-4">
            Requirements
          </h2>
          <ul className="space-y-3">
            {career.requirements.map((requirement, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-green-500 mt-1 flex-shrink-0">✓</span>
                <span className="text-gray-700">{requirement}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-white/90 rounded-2xl shadow-xl p-8 mb-12 border border-blue-100">
        <h2 className="text-xl font-bold text-blue-900 mb-4">
          Benefits & Perks
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {career.benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <span className="text-blue-500 mt-1 flex-shrink-0">★</span>
              <span className="text-gray-700">{benefit}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Application form */}
      <div className="bg-white/90 rounded-2xl shadow-xl p-8 border border-blue-100">
        <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">
          Apply to Join the Installation Team at Bidet New York
        </h2>
        <p className="text-gray-600 text-center mb-8">
          All employees at Bidet New York start as installers
        </p>

        <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                required
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                required
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="appEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email Address <span className="text-red-500">*</span>
            </label>
            <input
              id="appEmail"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="appPhone" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              id="appPhone"
              type="tel"
              required
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="whyWorkHere" className="block text-sm font-medium text-gray-700 mb-1">
              Why do you want to work here? <span className="text-red-500">*</span>
            </label>
            <textarea
              id="whyWorkHere"
              rows={4}
              required
              value={whyWorkHere}
              onChange={e => setWhyWorkHere(e.target.value)}
              className={inputClass + " resize-vertical"}
            />
          </div>

          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
              Do you have any relevant experience? <span className="text-red-500">*</span>
            </label>
            <textarea
              id="experience"
              rows={4}
              required
              value={experience}
              onChange={e => setExperience(e.target.value)}
              className={inputClass + " resize-vertical"}
            />
          </div>

          <div>
            <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-1">
              What's your level of education? <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Let us know if you completed high school, any secondary schools, or any other training, licensing, or certification
            </p>
            <textarea
              id="education"
              rows={3}
              required
              value={education}
              onChange={e => setEducation(e.target.value)}
              className={inputClass + " resize-vertical"}
            />
          </div>

          <div>
            <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">
              Resume
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Highly recommended. Drop files here or browse
            </p>
            <input
              id="resume"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={e => setResumeFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <div>
            <label htmlFor="otherDocs" className="block text-sm font-medium text-gray-700 mb-1">
              Other documentation
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Do you have anything else you'd like us to see as part of your application?
            </p>
            <input
              id="otherDocs"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.png"
              onChange={e => setOtherDocsFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting Application...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}
